import {
  capSupportMessage,
  STOREFRONT_SUPPORT_NAME_TOKEN,
} from "@/domain/storefront-appearance";

/**
 * Digits only, with Brazil country code when the number is national.
 *
 * Mirrors portal `phoneToWhatsAppDigits` — both open chat from the SAME
 * registration; diverging would make one link work and the other fail silently.
 * Numbers that already include `55` with 12+ digits pass through.
 */
export function whatsAppDigits(phone: string | null | undefined): string {
  if (!phone) return "";
  const cleaned = phone.replace(/\D/g, "");
  if (!cleaned) return "";
  if (cleaned.startsWith("55") && cleaned.length >= 12) return cleaned;
  if (cleaned.length === 10 || cleaned.length === 11) return `55${cleaned}`;
  return cleaned;
}

/**
 * Replaces {@link STOREFRONT_SUPPORT_NAME_TOKEN} with the customer name.
 * Without a name (common — login only at bag end) the token and orphan spacing
 * are removed so chat does not open with a broken greeting.
 */
export function expandSupportMessage(message: string, customerName: string | null | undefined): string {
  const name = (customerName ?? "").trim();
  const filled = message.split(STOREFRONT_SUPPORT_NAME_TOKEN).join(name);

  if (name) return filled.replace(/\s+/g, " ").trim();

  return filled
    .replace(/\s+/g, " ")
    .replace(/\s+([,.!?;:])/g, "$1")
    .replace(/^[\s,.!?;:]+/, "")
    .trim();
}

type WhatsAppLinkParams = {
  phone: string | null | undefined;
  message?: string;
};

/**
 * Universal click-to-chat link (`wa.me`), the format WhatsApp documents. With
 * the app installed the OS hands it off; without it, the page offers Web or
 * download.
 */
export function buildWhatsAppUrl({ phone, message }: WhatsAppLinkParams): string | null {
  const digits = whatsAppDigits(phone);
  if (!digits) return null;
  const text = capSupportMessage((message ?? "").replace(/\s+/g, " ").trim());
  const base = `https://wa.me/${digits}`;
  return text ? `${base}?text=${encodeURIComponent(text)}` : base;
}

/** Where the tap lands. iOS and Android open the app; `web` is desktop. */
export type WhatsAppPlatform = "ios" | "android" | "android-firefox" | "web";

type PlatformSource = Pick<Navigator, "userAgent" | "maxTouchPoints" | "platform">;

/**
 * Reads platform from `navigator`. iPadOS 13+ presents as Mac; only touch
 * gives it away. Android Firefox is separate — it does not understand
 * `intent://`; other Android browsers do.
 */
export function detectWhatsAppPlatform(source: PlatformSource = navigator): WhatsAppPlatform {
  const ua = source.userAgent;
  if (/iPad|iPhone|iPod/.test(ua) || (source.platform === "MacIntel" && source.maxTouchPoints > 1)) return "ios";
  if (/Android/i.test(ua)) return /Firefox/i.test(ua) ? "android-firefox" : "android";
  return "web";
}

/* ── A BANDEIRA ───────────────────────────────────────────────────────────── */

/**
 * Three ways to send the customer to WhatsApp, side by side for device
 * comparison. Each solves one thing and costs another:
 *
 * - `universal`: same-tab `wa.me` on mobile. OS hands to the app; storefront
 *   tab stays; dual WhatsApp/Business choice when both exist. If the OS stops
 *   honoring the link, the tab goes to WhatsApp's page and Back returns.
 * - `scheme`: `whatsapp://` on iPhone and Android Firefox, `intent://` with
 *   native fallback elsewhere on Android. Canceling "Open with WhatsApp?"
 *   navigates nowhere but skips the chooser.
 * - `new-tab`: `wa.me` in a new tab everywhere — original behavior. On iPhone
 *   the new tab often stops the OS from honoring the link.
 *
 * On desktop all three open WhatsApp Web in a new tab.
 */
export const WHATSAPP_LINK_MODES = ["universal", "scheme", "new-tab"] as const;

export type WhatsAppLinkMode = (typeof WHATSAPP_LINK_MODES)[number];

function isLinkMode(value: unknown): value is WhatsAppLinkMode {
  return WHATSAPP_LINK_MODES.includes(value as WhatsAppLinkMode);
}

/**
 * Mode comes from BUILD (`VITE_WHATSAPP_LINK_MODE`), no runtime override:
 * comparison ships each mode; unknown/missing falls back to `universal`.
 */
export function resolveWhatsAppLinkMode(envValue: string | undefined): WhatsAppLinkMode {
  const value = envValue?.trim();
  return isLinkMode(value) ? value : "universal";
}

export type WhatsAppLink = {
  href: string;
  target?: "_blank";
};

/** Support-button link from flag mode and platform (see {@link WHATSAPP_LINK_MODES}). */
export function buildWhatsAppLink(
  params: WhatsAppLinkParams,
  platform: WhatsAppPlatform,
  mode: WhatsAppLinkMode
): WhatsAppLink | null {
  const digits = whatsAppDigits(params.phone);
  if (!digits) return null;
  const web = buildWhatsAppUrl(params)!;
  const newTab: WhatsAppLink = { href: web, target: "_blank" };

  if (mode === "new-tab" || platform === "web") return newTab;
  if (mode === "universal") return { href: web };

  const text = capSupportMessage((params.message ?? "").replace(/\s+/g, " ").trim());
  const query = text ? `phone=${digits}&text=${encodeURIComponent(text)}` : `phone=${digits}`;
  if (platform === "android") {
    return {
      href: `intent://send?${query}#Intent;scheme=whatsapp;S.browser_fallback_url=${encodeURIComponent(web)};end`,
    };
  }
  return { href: `whatsapp://send?${query}` };
}
